/**
 * The nine values that survive a reload: two audio-device preferences plus a
 * display-name fallback, the machine-local Dictate microphone preference, the
 * web audio input, the open sidebar tab, the two resizable bar widths, and the
 * per-session EHR push timestamps.
 *
 * These nine have nothing in common except that they were persisted, which is
 * why the module they came from was named after its mechanism. They will
 * separate into four owners as the extractions reach them — audio-device prefs,
 * session UI prefs, shell layout, push timestamps — and nothing here assumes a
 * package home for any of them (design D4: a former slice's atoms live app-side
 * and cross into a package as explicit inputs).
 *
 * One atom per field, not one atom for the group. The open sidebar tab has 17
 * readers and both bar widths have one each; a single atom would re-render every
 * sidebar consumer on every drag frame of a bar resize.
 *
 * ## Persistence
 *
 * All nine were persisted before, in the shared `persist:root` redux-persist
 * blob. Each field now has its OWN frozen storage key under a `{ v, data }`
 * envelope (`@heidi/runtime/storage` `defineVersionedStorage`), which is what
 * makes a later shape change to one field a migration of that field rather than
 * of everything else that happened to be persisted at the same time.
 *
 * The keys keep the legacy slice's name — `heidi.persistedModel.<field>.v1`.
 * That name describes a mechanism rather than a domain and would not be chosen
 * fresh, but a storage key is a frozen contract: it is what makes each key
 * greppable against the blob entry it was seeded from, and renaming it later is
 * exactly the thing the versioning convention forbids. It is one vocabulary for
 * these nine values, not two.
 *
 * `runStorageMigrations` below seeds the nine keys from the legacy blob once
 * per browser profile. It runs at module scope, ABOVE the atom declarations,
 * because `getOnInit: true` reads storage when `atomWithStorage` is called — i.e.
 * during this module's evaluation. It reads the serialized JSON directly and
 * never goes through redux, it is tombstoned so it runs at most once, it never
 * overwrites a target that already has a value, and it leaves the legacy blob in
 * place: deleting that is a separate later release, gated on the desktop fleet
 * minimum version.
 *
 * Six of the nine fields are ABSENT from a fresh install's state rather than
 * `undefined` in it, and the migration reproduces that: a field the blob does
 * not carry seeds nothing, so its key stays absent and its fallback applies.
 *
 * `getOnInit: true` is required rather than decorative. `recorderSettings`'s
 * `buildOfflineCreateArgs` reads the last-used input device outside React, on a
 * path that can run before anything mounts; without it the offline recorder
 * would start on the wrong microphone.
 */
import { logger } from '@/utils/logger/logger';
import {
  defineVersionedStorage,
  encodeVersioned,
  runStorageMigrations,
  type StorageMigration,
  type VersionedStorage,
  type VersionedStorageEvent,
} from '@heidi/runtime/storage';
import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

export enum SidebarTab {
  SESSION_LIST = 'sessionList',
  ASK_HEIDI_V2 = 'askHeidiV2',
  // Add other sidebar tabs here in the future
}

export interface PersistedState {
  // Tauri
  tauriLastUsedInputDevice?: string;
  tauriLastUsedOutputDevice?: string;
  /** For process devices: display name (e.g. "Opera") for fallback when PID changes between sessions */
  tauriLastUsedOutputDeviceDisplayName?: string;
  /**
   * Machine-local Dictate microphone preference (shared across signed-in
   * accounts on this install). `undefined` = inherit session mic;
   * `SYSTEM_DEFAULT_DEVICE_MARKER` = explicit OS default; any other string =
   * physical CPAL device name.
   */
  tauriDictationInputDevicePreference?: string;

  // Audio
  lastUsedAudioInput?: string;

  // UI
  sidebarOpenTab?: SidebarTab;
  sessionsBarWidth?: number;
  askHeidiBarWidth?: number;

  // Session tracking
  sessionLastPushedTimes?: Record<string, number>;
}

export type PersistedPreferenceField = keyof PersistedState;

const INITIAL_STATE: PersistedState = {
  tauriLastUsedInputDevice: undefined,
  tauriLastUsedOutputDevice: undefined,
  tauriLastUsedOutputDeviceDisplayName: undefined,
  tauriDictationInputDevicePreference: undefined,
  lastUsedAudioInput: undefined,
  sidebarOpenTab: undefined,
  sessionsBarWidth: 10, // Default sessions bar width
  askHeidiBarWidth: 40, // Default ask heidi bar width
  sessionLastPushedTimes: {},
};

/**
 * One frozen storage key per field: explicit strings, never derived, never
 * renamed. Held in one map rather than eight constants so the set is enumerable
 * — the migrations below walk it, and a new field is a compile error in two
 * places at once.
 */
export const PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD: Record<
  PersistedPreferenceField,
  string
> = {
  tauriLastUsedInputDevice: 'heidi.persistedModel.tauriLastUsedInputDevice.v1',
  tauriLastUsedOutputDevice: 'heidi.persistedModel.tauriLastUsedOutputDevice.v1',
  tauriLastUsedOutputDeviceDisplayName:
    'heidi.persistedModel.tauriLastUsedOutputDeviceDisplayName.v1',
  tauriDictationInputDevicePreference:
    'heidi.persistedModel.tauriDictationInputDevicePreference.v1',
  lastUsedAudioInput: 'heidi.persistedModel.lastUsedAudioInput.v1',
  sidebarOpenTab: 'heidi.persistedModel.sidebarOpenTab.v1',
  sessionsBarWidth: 'heidi.persistedModel.sessionsBarWidth.v1',
  askHeidiBarWidth: 'heidi.persistedModel.askHeidiBarWidth.v1',
  sessionLastPushedTimes: 'heidi.persistedModel.sessionLastPushedTimes.v1',
};

const STORAGE_VERSION = 1;

/** The redux-persist blob these eight values were part of until now. */
const LEGACY_PERSIST_ROOT = 'persist:root';
const LEGACY_SLICE_NAME = 'persistedModel';

function reportStorageEvent(event: VersionedStorageEvent): void {
  // Counts, keys and versions only — never the stored value. None of these
  // eight is a credential, but `sessionLastPushedTimes` is keyed by session id.
  if (event.type === 'legacy-migration-failed') {
    logger.warn('[persistedPreferences] legacy storage migration failed', {
      id: event.id,
      legacyKey: event.legacyKey,
    });
    return;
  }
  if (event.type === 'legacy-skipped') {
    // Warn, not debug: the blob was readable and yielded nothing for this key,
    // and the run is tombstoned so it will never be retried. Per key that is
    // often benign — six of the nine are absent from a fresh install's slice —
    // but nine of nine in one boot is the whole slice already gone, which is
    // the case this event exists to make visible.
    logger.warn('[persistedPreferences] legacy storage migration seeded nothing', {
      id: event.id,
      legacyKey: event.legacyKey,
      targetKey: event.targetKey,
    });
    return;
  }
  if (event.type === 'fallback') {
    logger.warn('[persistedPreferences] persisted value unreadable, using fallback', {
      key: event.key,
      reason: event.reason,
    });
    return;
  }
  if (event.type === 'write-failed') {
    logger.warn('[persistedPreferences] persisted value could not be written', {
      key: event.key,
    });
    return;
  }
  logger.debug('[persistedPreferences] persisted value migrated', event);
}

/**
 * Reads `persist:root`'s double-encoded JSON and returns the legacy slice, or
 * `undefined` when the blob carries no `persistedModel` entry. Throws on an
 * unparseable blob, which the runner records as a failed migration.
 */
function readLegacySlice(rawLegacy: string): Partial<PersistedState> | undefined {
  const parsed = JSON.parse(rawLegacy) as Record<string, string | undefined>;
  const slice = parsed[LEGACY_SLICE_NAME];
  if (typeof slice !== 'string') return undefined;
  return JSON.parse(slice) as Partial<PersistedState>;
}

/**
 * One migration per key. A field the legacy blob does not carry seeds nothing,
 * so the target key stays absent and its fallback applies — which is the value
 * the slice resolved for a field its `initialState` never declared.
 */
export const PERSISTED_PREFERENCE_LEGACY_MIGRATIONS: readonly StorageMigration[] = (
  Object.keys(PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD) as PersistedPreferenceField[]
).map((field) => ({
  id: `persistedModel.${field}.from-persist-root.v1`,
  legacyKey: LEGACY_PERSIST_ROOT,
  targetKey: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD[field],
  transform: (rawLegacy: string): string | null => {
    const legacy = readLegacySlice(rawLegacy);
    if (!legacy || !(field in legacy)) return null;
    const value = legacy[field];
    if (value === undefined) return null;
    return encodeVersioned(STORAGE_VERSION, value);
  },
}));

runStorageMigrations(PERSISTED_PREFERENCE_LEGACY_MIGRATIONS, reportStorageEvent);

/**
 * `undefined` is a real value for the optional fields, and JSON has no
 * representation for it: `JSON.stringify({ v: 1, data: undefined })` drops
 * `data` entirely and the decoder would read every subsequent load as corrupt.
 * Writing `undefined` removes the key instead, so the next read falls back to
 * `undefined` — which is also how the redux-persist blob behaved, since it never
 * serialized an undefined field either.
 */
function withUndefinedAsAbsent<T>(storage: VersionedStorage<T>): VersionedStorage<T> {
  const write = (value: T): void => {
    if (value === undefined) {
      storage.clear();
      return;
    }
    storage.write(value);
  };

  return {
    ...storage,
    write,
    adapter: { ...storage.adapter, setItem: (_key: string, value: T) => write(value) },
  };
}

const tauriLastUsedInputDeviceStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriLastUsedInputDevice,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.tauriLastUsedInputDevice,
    onEvent: reportStorageEvent,
  })
);
export const tauriLastUsedInputDeviceAtom = atomWithStorage<string | undefined>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriLastUsedInputDevice,
  INITIAL_STATE.tauriLastUsedInputDevice,
  tauriLastUsedInputDeviceStorage.adapter,
  { getOnInit: true }
);

const tauriLastUsedOutputDeviceStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriLastUsedOutputDevice,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.tauriLastUsedOutputDevice,
    onEvent: reportStorageEvent,
  })
);
export const tauriLastUsedOutputDeviceAtom = atomWithStorage<string | undefined>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriLastUsedOutputDevice,
  INITIAL_STATE.tauriLastUsedOutputDevice,
  tauriLastUsedOutputDeviceStorage.adapter,
  { getOnInit: true }
);

const tauriLastUsedOutputDeviceDisplayNameStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriLastUsedOutputDeviceDisplayName,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.tauriLastUsedOutputDeviceDisplayName,
    onEvent: reportStorageEvent,
  })
);
export const tauriLastUsedOutputDeviceDisplayNameAtom = atomWithStorage<
  string | undefined
>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriLastUsedOutputDeviceDisplayName,
  INITIAL_STATE.tauriLastUsedOutputDeviceDisplayName,
  tauriLastUsedOutputDeviceDisplayNameStorage.adapter,
  { getOnInit: true }
);

const tauriDictationInputDevicePreferenceStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriDictationInputDevicePreference,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.tauriDictationInputDevicePreference,
    onEvent: reportStorageEvent,
  })
);
export const tauriDictationInputDevicePreferenceAtom = atomWithStorage<
  string | undefined
>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.tauriDictationInputDevicePreference,
  INITIAL_STATE.tauriDictationInputDevicePreference,
  tauriDictationInputDevicePreferenceStorage.adapter,
  { getOnInit: true }
);

const lastUsedAudioInputStorage = withUndefinedAsAbsent(
  defineVersionedStorage<string | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.lastUsedAudioInput,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.lastUsedAudioInput,
    onEvent: reportStorageEvent,
  })
);
export const lastUsedAudioInputAtom = atomWithStorage<string | undefined>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.lastUsedAudioInput,
  INITIAL_STATE.lastUsedAudioInput,
  lastUsedAudioInputStorage.adapter,
  { getOnInit: true }
);

const sidebarOpenTabStorage = withUndefinedAsAbsent(
  defineVersionedStorage<SidebarTab | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.sidebarOpenTab,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.sidebarOpenTab,
    onEvent: reportStorageEvent,
  })
);
export const sidebarOpenTabAtom = atomWithStorage<SidebarTab | undefined>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.sidebarOpenTab,
  INITIAL_STATE.sidebarOpenTab,
  sidebarOpenTabStorage.adapter,
  { getOnInit: true }
);

const sessionsBarWidthStorage = withUndefinedAsAbsent(
  defineVersionedStorage<number | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.sessionsBarWidth,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.sessionsBarWidth,
    onEvent: reportStorageEvent,
  })
);
export const sessionsBarWidthAtom = atomWithStorage<number | undefined>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.sessionsBarWidth,
  INITIAL_STATE.sessionsBarWidth,
  sessionsBarWidthStorage.adapter,
  { getOnInit: true }
);

const askHeidiBarWidthStorage = withUndefinedAsAbsent(
  defineVersionedStorage<number | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.askHeidiBarWidth,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.askHeidiBarWidth,
    onEvent: reportStorageEvent,
  })
);
export const askHeidiBarWidthAtom = atomWithStorage<number | undefined>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.askHeidiBarWidth,
  INITIAL_STATE.askHeidiBarWidth,
  askHeidiBarWidthStorage.adapter,
  { getOnInit: true }
);

const sessionLastPushedTimesStorage = withUndefinedAsAbsent(
  defineVersionedStorage<Record<string, number> | undefined>({
    key: PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.sessionLastPushedTimes,
    version: STORAGE_VERSION,
    fallback: INITIAL_STATE.sessionLastPushedTimes,
    onEvent: reportStorageEvent,
  })
);
export const sessionLastPushedTimesAtom = atomWithStorage<
  Record<string, number> | undefined
>(
  PERSISTED_PREFERENCE_STORAGE_KEY_BY_FIELD.sessionLastPushedTimes,
  INITIAL_STATE.sessionLastPushedTimes,
  sessionLastPushedTimesStorage.adapter,
  { getOnInit: true }
);

/**
 * The envelope behind each atom, keyed by field. Exposed because the repo's jest
 * `jotai/utils` mock replaces `atomWithStorage` with an implementation that
 * ignores the storage argument, so the versioned contract is only checkable
 * here; `read`/`write` are also the door a non-atom caller uses.
 */
export const persistedPreferenceStorage = {
  tauriLastUsedInputDevice: tauriLastUsedInputDeviceStorage,
  tauriLastUsedOutputDevice: tauriLastUsedOutputDeviceStorage,
  tauriLastUsedOutputDeviceDisplayName: tauriLastUsedOutputDeviceDisplayNameStorage,
  tauriDictationInputDevicePreference: tauriDictationInputDevicePreferenceStorage,
  lastUsedAudioInput: lastUsedAudioInputStorage,
  sidebarOpenTab: sidebarOpenTabStorage,
  sessionsBarWidth: sessionsBarWidthStorage,
  askHeidiBarWidth: askHeidiBarWidthStorage,
  sessionLastPushedTimes: sessionLastPushedTimesStorage,
};

/** The whole shape, for readers that want more than one field. */
export const persistedPreferencesAtom = atom<PersistedState>((get) => ({
  tauriLastUsedInputDevice: get(tauriLastUsedInputDeviceAtom),
  tauriLastUsedOutputDevice: get(tauriLastUsedOutputDeviceAtom),
  tauriLastUsedOutputDeviceDisplayName: get(tauriLastUsedOutputDeviceDisplayNameAtom),
  tauriDictationInputDevicePreference: get(tauriDictationInputDevicePreferenceAtom),
  lastUsedAudioInput: get(lastUsedAudioInputAtom),
  sidebarOpenTab: get(sidebarOpenTabAtom),
  sessionsBarWidth: get(sessionsBarWidthAtom),
  askHeidiBarWidth: get(askHeidiBarWidthAtom),
  sessionLastPushedTimes: get(sessionLastPushedTimesAtom),
}));

/**
 * `!== undefined`, not truthiness. Both current `SidebarTab` members are truthy
 * strings so the two agree today, and they would stop agreeing the first time a
 * falsy member is added — which is the kind of divergence nothing would
 * attribute to this conversion.
 */
export const isAnySidebarTabOpenAtom = atom<boolean>(
  (get) => get(sidebarOpenTabAtom) !== undefined
);

export function setTauriLastUsedInputDevice(tauriLastUsedInputDevice?: string): void {
  getDefaultStore().set(tauriLastUsedInputDeviceAtom, tauriLastUsedInputDevice);
}

export function setTauriLastUsedOutputDevice(tauriLastUsedOutputDevice?: string): void {
  getDefaultStore().set(tauriLastUsedOutputDeviceAtom, tauriLastUsedOutputDevice);
}

export function setTauriLastUsedOutputDeviceDisplayName(
  tauriLastUsedOutputDeviceDisplayName?: string
): void {
  getDefaultStore().set(
    tauriLastUsedOutputDeviceDisplayNameAtom,
    tauriLastUsedOutputDeviceDisplayName
  );
}

export function setTauriDictationInputDevicePreference(
  tauriDictationInputDevicePreference?: string
): void {
  getDefaultStore().set(
    tauriDictationInputDevicePreferenceAtom,
    tauriDictationInputDevicePreference
  );
}

export function setLastUsedAudioInput(lastUsedAudioInput?: string): void {
  getDefaultStore().set(lastUsedAudioInputAtom, lastUsedAudioInput);
}

export function setSidebarOpenTab(sidebarOpenTab?: SidebarTab): void {
  getDefaultStore().set(sidebarOpenTabAtom, sidebarOpenTab);
}

export function setSessionsBarWidth(sessionsBarWidth: number): void {
  getDefaultStore().set(sessionsBarWidthAtom, sessionsBarWidth);
}

export function setAskHeidiBarWidth(askHeidiBarWidth: number): void {
  getDefaultStore().set(askHeidiBarWidthAtom, askHeidiBarWidth);
}

/** MERGES into the map; an absent session id keeps every other entry. */
export function setSessionLastPushedTime(payload: {
  sessionId: string;
  timestamp: number;
}): void {
  const store = getDefaultStore();
  store.set(sessionLastPushedTimesAtom, {
    ...(store.get(sessionLastPushedTimesAtom) ?? {}),
    [payload.sessionId]: payload.timestamp,
  });
}

const SESSION_EXPIRY_MS = 48 * 60 * 60 * 1000; // 48 hours

/**
 * Drops push timestamps older than 48 hours, and WRITES NOTHING when none are.
 *
 * The early return is the point, not an optimisation: the reducer this replaces
 * returned the same state reference in that case, which kept redux-persist from
 * writing. This sweep runs on a timer, so an unconditional write would put a
 * storage write on every tick of an idle session.
 */
export function clearOldSessionPushTimes(): void {
  const store = getDefaultStore();
  const currentMap = store.get(sessionLastPushedTimesAtom) ?? {};
  const currentTime = Date.now();

  let hasChanged = false;
  const cleanedMap = Object.entries(currentMap).reduce(
    (acc, [sessionId, timestamp]) => {
      if (currentTime - timestamp < SESSION_EXPIRY_MS) {
        acc[sessionId] = timestamp;
      } else {
        hasChanged = true;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  if (!hasChanged) return;
  store.set(sessionLastPushedTimesAtom, cleanedMap);
}

/**
 * The last-used Tauri input device, for `buildOfflineCreateArgs`. Not a hook:
 * the offline recorder assembles its create args outside React, on a path that
 * can run before anything has mounted.
 */
export function getTauriLastUsedInputDevice(): string | undefined {
  return getDefaultStore().get(tauriLastUsedInputDeviceAtom);
}
